import {
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output,
	ViewChild
} from '@angular/core';
import { FormGroup, NgForm, NG_VALIDATORS, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { ProducerService } from 'rev-shared/webcast/webcastView/producer/Producer.Service';

import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel, IEditPresenter } from './EditWebcastModel';

import styles from './EditWebcast.module.less';

@Component({
	selector: 'edit-external-presenters',
	templateUrl: './EditExternalPresenters.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditExternalPresentersComponent),
		multi: true
	}],
	standalone: false
})
export class EditExternalPresentersComponent extends EditWebcastBaseSettingsComponent {
	@Input() public webcast: EditWebcastModel;
	@Output() public onAddRemove = new EventEmitter();

	@ViewChild(NgForm) public form: NgForm;

	public readonly styles = styles;

	public hasEdits: boolean;

	constructor(
		private readonly DialogService: DialogService,
		private readonly ProducerService: ProducerService,
		private readonly TranslateService: TranslateService
	) {
		super();
	}

	public readonly trackByIndex = (i: number) => {
		//keep end of list stable as new items are prepended
		return this.webcast.externalPresenters.length - i;
	};

	public get addEnabled() {
		const first = this.webcast.externalPresenters[0];
		return !first ||
			!first.isNew ||
			!first.isEdit;
	}

	public editPresenter(p: IEditPresenter): void {
		p.edits = { ...p.data };
		p.isEdit = true;
		this.hasEdits = true;
		this.validateFn();
	}

	public applyPresenterEdits(presenter: IEditPresenter) {
		presenter.data = { ...presenter.edits };
		presenter.isEdit = false;
		this.validateGroup();
	}

	public cancelPresenterEdit(p: IEditPresenter, index: number): void {
		delete p.edits;
		p.isEdit = false;
		if(!p.data.name || !p.data.email) {
			this.removeExternalPresenter(index);
		}
		setTimeout(() => this.validateGroup(), 200);
	}

	public addExternalPresenter(): void {
		this.webcast.externalPresenters.unshift({
			isEdit: true,
			isNew: true,
			edits: {} as any,
			data: {} as any
		});
		this.onAddRemove.emit();
	}

	public removeExternalPresenter(index: number): void {
		this.webcast.externalPresenters.splice(index, 1);
		this.onAddRemove.emit();
	}

	public validateGroup() {
		const len = this.webcast.externalPresenters.length;

		this.hasEdits = false;
		this.webcast.externalPresenters.forEach((p, i) => {
			if(!p.isEdit) {
				return;
			}
			this.hasEdits = true;
			const email = p.edits.email;
			const emailValid = this.webcast.externalPresenters.every(u => p === u || email !== (u.isEdit ? u.edits.email : u.data.email));
			const formGroup = this.form.controls['presenter' + (len - i)] as FormGroup;

			toggleError(formGroup.controls.email, 'emailInUse', !emailValid);
		});

		this.validateFn();
	}

	public validate(): ValidationErrors {
		if(this.hasEdits) {
			return { hasEdits: this.hasEdits };
		}
		return super.validate();
	}

	public resendEmail(presenter: IEditPresenter) {
		if(presenter.sendingEmail) {
			return;
		}

		presenter.sendingEmail = true;

		this.ProducerService.resendExternalPresenterEmail(this.webcast.id, presenter.data)
			.then(() => this.DialogService.openConfirmationDialog({
				title: this.TranslateService.instant('ResendEmail'),
				message: this.TranslateService.instant('ResendEmail_Success'),
				actionText: this.TranslateService.instant('Ok')
			}).result)
			.catch(e => {
				console.error('Error sending presenter email: ', e);
				this.DialogService.openAlertDialog(
					this.TranslateService.instant('Error'),
					this.TranslateService.instant('UnhandledError'),
					this.TranslateService.instant('Ok'));
			})
			.finally(() => {
				presenter.sendingEmail = false;
			});
	}

}
